/**
 * Company Admin View
 *
 * Multi-company management: company selector dropdown, "New Company" button,
 * and editable form for the active company's details.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type CompanyEntry = {
  id: string;
  name: string | null;
  description: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  vatNumber: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type VatNumberEntry = {
  id: string;
  companyId: string;
  country: string;
  vatNumber: string;
  vatRate: number;
  isDefault: boolean;
};

export type CompanyProps = {
  loading: boolean;
  error: string | null;
  saving: boolean;
  company: CompanyEntry | null;
  form: Partial<CompanyEntry> | null;
  companies: CompanyEntry[];
  activeCompanyId: string | null;
  vatNumbers: VatNumberEntry[];
  onRefresh: () => void;
  onSave: () => void;
  onFormChange: (field: string, value: string) => void;
  onSelectCompany: (id: string) => void;
  onCreateCompany: () => void;
  onAddVatNumber: (country: string, vatNumber: string, vatRate: number) => void;
  onDeleteVatNumber: (id: string) => void;
};

// --------------- Helpers ---------------

function fieldRow(
  label: string,
  field: string,
  value: string,
  onChange: (field: string, value: string) => void,
  opts?: { type?: string; placeholder?: string; wide?: boolean },
) {
  const type = opts?.type ?? "text";
  const placeholder = opts?.placeholder ?? "";
  const wide = opts?.wide ?? false;
  return html`
    <div style="${wide ? "grid-column:1/-1;" : ""}">
      <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">${label}</label>
      <input
        type=${type}
        class="input"
        style="width:100%;box-sizing:border-box;"
        .value=${value}
        placeholder=${placeholder}
        @input=${(e: Event) => onChange(field, (e.target as HTMLInputElement).value)}
      />
    </div>
  `;
}

function textareaRow(
  label: string,
  field: string,
  value: string,
  onChange: (field: string, value: string) => void,
) {
  return html`
    <div style="grid-column:1/-1;">
      <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">${label}</label>
      <textarea
        class="input"
        style="width:100%;box-sizing:border-box;min-height:80px;resize:vertical;font-family:inherit;"
        .value=${value}
        @input=${(e: Event) => onChange(field, (e.target as HTMLTextAreaElement).value)}
      ></textarea>
    </div>
  `;
}

// --------------- Render ---------------

export function renderCompany(props: CompanyProps) {
  const form = props.form;
  const hasMultiple = props.companies.length > 1;

  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">
      <!-- Company Selector -->
      <div class="card">
        <div class="card-title">Company</div>
        <div class="card-sub">Select the active company or create a new one. The active company serves as the primary context for all operations.</div>
        <div style="margin-top:16px;display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;">
          <!-- Selector dropdown -->
          <div style="flex:1;min-width:220px;max-width:400px;">
            <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Active Company</label>
            <select
              class="input"
              style="width:100%;box-sizing:border-box;padding:8px 12px;"
              @change=${(e: Event) => props.onSelectCompany((e.target as HTMLSelectElement).value)}
              ?disabled=${props.loading}
            >
              ${
                props.companies.length === 0
                  ? html`
                      <option value="" disabled selected>No companies yet</option>
                    `
                  : nothing
              }
              ${props.companies.map(
                (c) => html`
                  <option value=${c.id} ?selected=${c.id === props.activeCompanyId}>
                    ${c.name || "(Unnamed Company)"}${c.isActive ? " (Active)" : ""}
                  </option>
                `,
              )}
            </select>
          </div>
          <!-- Action buttons -->
          <div style="display:flex;gap:8px;padding-bottom:2px;">
            <button
              class="btn primary"
              @click=${props.onCreateCompany}
              ?disabled=${props.loading || props.saving}
            >
              + New Company
            </button>
            <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
              ${props.loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
        ${
          hasMultiple
            ? html`<div style="margin-top:10px;font-size:0.8em;color:var(--fg-muted);">
              ${props.companies.length} companies registered
            </div>`
            : nothing
        }
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        props.loading && !form
          ? html`
              <div class="callout info">Loading company details...</div>
            `
          : nothing
      }

      ${
        form
          ? html`
            <!-- Header with Save button -->
            <div style="display:flex;justify-content:flex-end;align-items:center;">
              <button
                class="btn primary"
                @click=${props.onSave}
                ?disabled=${props.saving || props.loading || !form}
              >
                ${props.saving ? "Saving..." : "Save"}
              </button>
            </div>

            <!-- Identity -->
            <div class="card">
              <div class="card-title">Identity</div>
              <div class="card-sub">Company name and description.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:1fr;gap:16px;">
                ${fieldRow("Company Name", "name", form.name ?? "", props.onFormChange, {
                  placeholder: "Acme Inc.",
                })}
                ${textareaRow(
                  "Description",
                  "description",
                  form.description ?? "",
                  props.onFormChange,
                )}
              </div>
            </div>

            <!-- Address -->
            <div class="card">
              <div class="card-title">Address</div>
              <div class="card-sub">Physical or registered address.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${fieldRow("Street Address", "address", form.address ?? "", props.onFormChange, {
                  placeholder: "123 Main St",
                  wide: true,
                })}
                ${fieldRow("City", "city", form.city ?? "", props.onFormChange, {
                  placeholder: "New York",
                })}
                ${fieldRow("State / Province", "state", form.state ?? "", props.onFormChange, {
                  placeholder: "NY",
                })}
                ${fieldRow("ZIP / Postal Code", "zipCode", form.zipCode ?? "", props.onFormChange, {
                  placeholder: "10001",
                })}
                ${fieldRow("Country", "country", form.country ?? "", props.onFormChange, {
                  placeholder: "United States",
                })}
              </div>
            </div>

            <!-- Contact & Legal -->
            <div class="card">
              <div class="card-title">Contact & Legal</div>
              <div class="card-sub">Contact information, website, and tax identification.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${fieldRow("Email", "email", form.email ?? "", props.onFormChange, {
                  type: "email",
                  placeholder: "info@example.com",
                })}
                ${fieldRow("Phone", "phone", form.phone ?? "", props.onFormChange, {
                  type: "tel",
                  placeholder: "+1 555-0100",
                })}
                ${fieldRow("Website", "website", form.website ?? "", props.onFormChange, {
                  type: "url",
                  placeholder: "https://example.com",
                })}
                ${fieldRow(
                  "VAT / Tax Number",
                  "vatNumber",
                  form.vatNumber ?? "",
                  props.onFormChange,
                  {
                    placeholder: "EU123456789",
                  },
                )}
              </div>
            </div>

            <!-- VAT Numbers (multiple per company for EU cross-border) -->
            ${
              props.company
                ? html`
                  <div class="card">
                    <div class="card-title" style="font-size:0.9em;">VAT Numbers</div>
                    <div class="card-sub" style="font-size:0.75em;margin-bottom:8px;">
                      Multiple VAT numbers for EU cross-border billing. Each entry has a country, VAT number, and rate.
                    </div>
                    <div class="list">
                      ${props.vatNumbers.length === 0
                        ? html`<div class="muted" style="padding:8px;">No VAT numbers configured.</div>`
                        : props.vatNumbers.map((v) => html`
                          <div class="list-item" style="padding:6px 0;">
                            <div class="list-main">
                              <div class="list-title" style="font-size:0.85em;">${v.country} ${v.isDefault ? "(default)" : ""}</div>
                              <div class="list-sub" style="font-size:0.8em;">${v.vatNumber} · ${Number(v.vatRate)}% VAT</div>
                            </div>
                            <button
                              class="btn btn--sm"
                              title="Delete VAT number"
                              @click=${() => props.onDeleteVatNumber(v.id)}
                            >✕</button>
                          </div>
                        `)}
                    </div>
                    <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;">
                      <input
                        type="text"
                        id="vat-country-input"
                        placeholder="Country (e.g. DE)"
                        style="width:80px;font-size:0.85em;padding:4px 8px;"
                      />
                      <input
                        type="text"
                        id="vat-number-input"
                        placeholder="VAT number"
                        style="flex:1;min-width:150px;font-size:0.85em;padding:4px 8px;"
                      />
                      <input
                        type="number"
                        id="vat-rate-input"
                        placeholder="Rate %"
                        min="0"
                        max="100"
                        step="1"
                        style="width:70px;font-size:0.85em;padding:4px 8px;"
                      />
                      <button
                        class="btn btn--sm"
                        @click=${() => {
                          const countryEl = document.getElementById("vat-country-input") as HTMLInputElement | null;
                          const numberEl = document.getElementById("vat-number-input") as HTMLInputElement | null;
                          const rateEl = document.getElementById("vat-rate-input") as HTMLInputElement | null;
                          if (countryEl?.value && numberEl?.value) {
                            props.onAddVatNumber(countryEl.value, numberEl.value, parseInt(rateEl?.value ?? "19", 10) || 19);
                            countryEl.value = "";
                            numberEl.value = "";
                            if (rateEl) rateEl.value = "";
                          }
                        }}
                      >+ Add</button>
                    </div>
                  </div>
                `
                : nothing
            }

            <!-- Timestamps (read-only) -->
            ${
              props.company
                ? html`
                  <div class="card" style="opacity:0.7;">
                    <div class="card-title" style="font-size:0.9em;">Record Info</div>
                    <div style="margin-top:8px;display:flex;gap:24px;font-size:0.85em;color:var(--fg-muted);flex-wrap:wrap;">
                      <span>ID: ${props.company.id}</span>
                      <span>Created: ${new Date(props.company.createdAt).toLocaleString()}</span>
                      <span>Updated: ${new Date(props.company.updatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                `
                : nothing
            }
          `
          : nothing
      }
    </div>
  `;
}
